import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { waApi } from '../../api/whatsapp'

export interface RenameGroupModalProps {
  companyId: string
  chatId: string
  currentName: string
  onClose: () => void
}

export function RenameGroupModal({ companyId, chatId, currentName, onClose }: RenameGroupModalProps) {
  const qc = useQueryClient()
  const [name, setName] = useState(currentName)
  const [error, setError] = useState('')

  const rename = useMutation({
    mutationFn: (subject: string) => waApi.renameGroup(companyId, chatId, subject),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wa-chat', companyId, chatId] })
      qc.invalidateQueries({ queryKey: ['wa-chats', companyId] })
      toast.success('Group renamed')
      onClose()
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Failed to rename group'),
  })

  const trimmed = name.trim()
  const canSubmit = !!trimmed && trimmed !== currentName
  const submit = () => { if (canSubmit) rename.mutate(trimmed) }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-[26rem] rounded-lg bg-white p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="mb-3 font-semibold">Rename group</h3>
        <label className="label">Group name</label>
        <input
          className="input"
          autoFocus
          maxLength={100}
          value={name}
          onChange={e => { setName(e.target.value); setError('') }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
        />
        <p className="mt-1 text-xs text-gray-500">This changes the group's name on WhatsApp for everyone.</p>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSubmit || rename.isPending} onClick={submit}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
